import * as React from "react";
import { inject, observer } from "mobx-react";
import { MobxComponent } from "../../../../../mobx/components";
import { FormGroup, Input } from "@lib/components";
import { withTranslation, WithTranslation } from "react-i18next";
import styled from "styled-components";

export type BookingCustomerPick = {
	_id: string;
	name: string;
	email: string;
	phone: string;
};

interface Props extends WithTranslation {
	active: boolean;
	restaurantId: string;
	onPick: (c: BookingCustomerPick) => void;
	/** Clear linked customer (parent passes when a customer is selected). */
	onClear?: () => void;
	showClear?: boolean;
}

interface State {
	query: string;
	results: T.Schema.Customer.CustomerSchema[];
	loading: boolean;
	openPanel: boolean;
	/** Trimmed query the current `results` belong to; "" until a search completes. */
	searchedFor: string;
	linkedName: string;
}

/** Wait for the user to pause typing before hitting the API (feedback: 1-2 s). */
const SEARCH_DEBOUNCE_MS = 1000;
const MIN_QUERY_LENGTH = 2;

const SearchWrap = styled.div`
	position: relative;
	margin-bottom: 20px;
	padding-bottom: 20px;
	border-bottom: 1px dashed #e2e8f0;
`;

const ResultsPanel = styled.div`
	position: absolute;
	left: 0;
	right: 0;
	top: 100%;
	margin-top: 4px;
	max-height: 220px;
	overflow-y: auto;
	z-index: 20;
	background: #fff;
	border: 1px solid #e2e8f0;
	border-radius: 1rem;
	box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12);
`;

const ResultRow = styled.button`
	display: block;
	width: 100%;
	text-align: left;
	padding: 0.65rem 0.85rem;
	border: none;
	border-bottom: 1px solid #f1f5f9;
	background: #fff;
	cursor: pointer;
	transition: background 0.12s ease;
	&:last-child {
		border-bottom: none;
	}
	&:hover {
		background: #f8fafc;
	}
`;

const ResultName = styled.div`
	font-size: 0.875rem;
	font-weight: 700;
	color: #0f172a;
`;

const ResultMeta = styled.div`
	font-size: 0.6875rem;
	color: #64748b;
	margin-top: 2px;
`;

const LinkedBar = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.75rem;
	margin-top: 8px;
	padding: 7px 10px;
	background: #f8fafc;
	border: 1px solid #e2e8f0;
	border-radius: 8px;
`;

const LinkedMeta = styled.div`
	min-width: 0;
	font-size: 0.8125rem;
	line-height: 1.3;
	color: #334155;
`;

const LinkedLabel = styled.span`
	display: block;
	font-size: 0.6875rem;
	font-weight: 600;
	color: #64748b;
`;

const LinkedName = styled.span`
	display: block;
	font-weight: 600;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const ClearLink = styled.button`
	flex-shrink: 0;
	border: none;
	background: none;
	padding: 0;
	font-size: 0.8125rem;
	font-weight: 600;
	color: #64748b;
	cursor: pointer;
	&:hover {
		color: #0f172a;
	}
`;

@inject("store")
@observer
class BookingCustomerSearchClass extends MobxComponent<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			query: "",
			results: [],
			loading: false,
			openPanel: false,
			searchedFor: "",
			linkedName: "",
		};
	}

	private debounceTimer: number | undefined;
	/** Incremented per search; responses for an older sequence are ignored. */
	private searchSeq = 0;

	componentDidUpdate(prevProps: Props) {
		if (prevProps.active && !this.props.active) {
			this.cancelPendingSearch();
			this.setState({
				query: "",
				results: [],
				openPanel: false,
				loading: false,
				searchedFor: "",
				linkedName: "",
			});
		}
	}

	componentWillUnmount() {
		this.cancelPendingSearch();
	}

	private cancelPendingSearch() {
		if (this.debounceTimer) window.clearTimeout(this.debounceTimer);
		this.debounceTimer = undefined;
		// Any in-flight response now belongs to a stale sequence.
		this.searchSeq += 1;
	}

	runSearch = async (q: string) => {
		const trimmed = q.trim();
		if (trimmed.length < MIN_QUERY_LENGTH) {
			this.setState({ results: [], loading: false, openPanel: false, searchedFor: "" });
			return;
		}
		const seq = ++this.searchSeq;
		this.setState({ loading: true });
		try {
			const res = await this.injected.store.api.customers_find({
				page: 1,
				limit: 25,
				sort: { created: -1 },
				search: trimmed,
				query: { restaurants: this.props.restaurantId },
			});
			if (seq !== this.searchSeq) return;
			if (res.outcome !== 0) {
				this.setState({ results: [], loading: false, openPanel: false, searchedFor: "" });
				return;
			}
			this.setState({
				results: res.items,
				loading: false,
				openPanel: true,
				searchedFor: trimmed,
			});
		} catch {
			if (seq !== this.searchSeq) return;
			this.setState({ results: [], loading: false, openPanel: false, searchedFor: "" });
		}
	};

	handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const q = e.target.value;
		// Typing invalidates whatever is on screen: hide old results and any pending request
		// so the panel cannot show stale matches and "No matches" for different queries.
		this.cancelPendingSearch();
		const tooShort = q.trim().length < MIN_QUERY_LENGTH;
		this.setState({
			query: q,
			results: [],
			openPanel: false,
			searchedFor: "",
			loading: false,
		});
		if (tooShort) return;
		this.debounceTimer = window.setTimeout(() => this.runSearch(q), SEARCH_DEBOUNCE_MS);
	};

	handlePick = (row: T.Schema.Customer.CustomerSchema) => {
		const pick: BookingCustomerPick = {
			_id: row._id,
			name: row.details?.name || "",
			email: (row.details?.email || row.login || "").trim(),
			phone: (row.details?.phone || "").trim(),
		};
		this.props.onPick(pick);
		this.cancelPendingSearch();
		this.setState({
			query: "",
			results: [],
			openPanel: false,
			searchedFor: "",
			loading: false,
			linkedName: pick.name || pick.email || pick.phone || "",
		});
	};

	handleClearSelection = () => {
		this.props.onClear?.();
		this.cancelPendingSearch();
		this.setState({ query: "", results: [], openPanel: false, searchedFor: "", loading: false, linkedName: "" });
	};

	render() {
		const { t, showClear, onClear } = this.props;
		const { query, results, loading, openPanel, searchedFor, linkedName } = this.state;
		// Results / empty state only describe the query currently in the box.
		const resultsCurrent = openPanel && searchedFor !== "" && searchedFor === query.trim();
		const showResults = resultsCurrent && results.length > 0;
		const showNoMatches = resultsCurrent && !loading && results.length === 0;
		return (
			<SearchWrap>
				<FormGroup
					no_border
					optional
					style={{ marginBottom: 0, paddingBottom: 0 }}
					title={t("Find customer", { defaultValue: "Find customer" })}
					help={t("bookingCustomerSearch.help", {
						defaultValue: "Search by name or email (min. 2 characters).",
					})}
				>
					<Input
						value={query}
						onChange={this.handleQueryChange}
						placeholder={t("Search by name, email, phone...", {
							defaultValue: "Search by name, email, phone...",
						})}
						autoComplete="off"
					/>
				</FormGroup>
				{showClear && onClear ? (
					<LinkedBar>
						<LinkedMeta>
							<LinkedLabel>
								{t("Linked customer", { defaultValue: "Linked customer" })}
							</LinkedLabel>
							<LinkedName className="notranslate">
								{linkedName || t("Customer selected", { defaultValue: "Customer selected" })}
							</LinkedName>
						</LinkedMeta>
						<ClearLink type="button" onClick={this.handleClearSelection}>
							{t("Clear", { defaultValue: "Clear" })}
						</ClearLink>
					</LinkedBar>
				) : null}
				{loading && (
					<p className="tiny text-muted m-t-1 m-b-0">
						{t("Searching...", { defaultValue: "Searching..." })}
					</p>
				)}
				{showResults && (
					<ResultsPanel role="listbox">
						{results.map((row) => (
							<ResultRow
								key={row._id}
								type="button"
								onClick={() => this.handlePick(row)}
								className="notranslate"
							>
								<ResultName>
									{row.details?.name || row.login || "—"}
								</ResultName>
								<ResultMeta>
									{row.details?.email || row.login || "—"}
								</ResultMeta>
								{row.details?.phone ? (
									<ResultMeta>{row.details.phone}</ResultMeta>
								) : null}
							</ResultRow>
						))}
					</ResultsPanel>
				)}
				{showNoMatches && (
					<p className="tiny text-muted m-t-1 m-b-0">
						{t("No matches", { defaultValue: "No matches" })}
					</p>
				)}
			</SearchWrap>
		);
	}
}

export const BookingCustomerSearch = withTranslation()(BookingCustomerSearchClass);
